import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { interval } from 'rxjs';
import { switchMap, filter, first, timeout, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * PDF data for display in viewer
 */
export interface PDFData {
  blob: Blob;
  blobUrl: string; // Blob URL for ng2-pdf-viewer
  loadedAt: Date;
  isLoading: boolean;
  error?: string;
}

/**
 * Response from /execution_data API when fetching PDF
 */
export interface ExecutionDataResponse {
  status: string;
  data?: Blob;
  error?: string;
  message?: string;
}

export interface ExtractionRun {
  id: string;
  fileName: string;
  pages?: number;
  documentModel: string;
  ocrMode: string;
  llmModel: string;
  status: 'Running' | 'Completed' | 'Failed' | 'Pending';
  startedDate: Date;
  completedDate?: Date | null;
  duration: string;
  durationMs: number;
  extractedData?: Record<string, any>;
  pdfData?: PDFData;
}

@Injectable({
  providedIn: 'root'
})
export class ExtractionRunService {
  private readonly STORAGE_KEY = 'extractionRuns';
  private readonly TOAST_SHOWN_KEY = 'completionToastsShown';
  
  private readonly recentRunsSubject = new BehaviorSubject<ExtractionRun[]>([]);
  public readonly recentRuns$ = this.recentRunsSubject.asObservable();

  private selectedRunSubject = new BehaviorSubject<ExtractionRun | null>(null);
  public selectedRun$ = this.selectedRunSubject.asObservable();

  // PDF loading state observable for UI feedback
  private pdfLoadingSubject = new Subject<{ runId: string; isLoading: boolean; error?: string }>();
  public pdfLoading$ = this.pdfLoadingSubject.asObservable();

  // PDF cache - prevents re-fetching the same PDF
  private pdfCache = new Map<string, PDFData>();

  // Track which runs have already shown their completion toast
  private completionToastShownSet = new Set<string>();

  // Subject to emit when a run's polling completes (COMPLETED status received)
  private extractionCompleted$ = new Subject<{ runId: string; response: any }>();
  public extractionCompleted = this.extractionCompleted$.asObservable();

  // Centralized timer management - emits formatted duration for each run every 1 second
  private timersSubject = new BehaviorSubject<Map<string, string>>(new Map());
  public timers$ = this.timersSubject.asObservable();
  
  // Track active timer intervals per run ID
  private timerIntervals = new Map<string, any>();

  // Track navigation source for dynamic back button tooltip
  private navigationSource: string = ''; // 'extraction-reports' or 'data-extraction'

  // API configuration
  private apiUrlV1 = `${environment.apiBaseUrl}/api/v1`;

  constructor(private http: HttpClient, private ngZone: NgZone) {
    // Load extraction runs from sessionStorage on initialization
    this.loadRunsFromStorage();
    this.loadToastStateFromStorage();
  }

  /**
   * Load extraction runs from sessionStorage
   */
  private loadRunsFromStorage(): void {
    try {
      const storedRuns = sessionStorage.getItem(this.STORAGE_KEY);
      if (storedRuns) {
        const runs = JSON.parse(storedRuns) as ExtractionRun[];
        // Convert date strings back to Date objects
        const hydratedRuns = runs.map(run => ({
          ...run,
          startedDate: new Date(run.startedDate),
          completedDate: run.completedDate ? new Date(run.completedDate) : null
        }));
        this.recentRunsSubject.next(hydratedRuns);
      }
    } catch (error) {
      // If there's an error, start with empty array
      this.recentRunsSubject.next([]);
    }
  }

  /**
   * Save extraction runs to sessionStorage
   */
  private saveRunsToStorage(): void {
    try {
      const runs = this.recentRunsSubject.value;
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(runs));
    } catch (error) {
    }
  }

  /**
   * Load completion toast state from sessionStorage
   */
  private loadToastStateFromStorage(): void {
    try {
      const storedToasts = sessionStorage.getItem(this.TOAST_SHOWN_KEY);
      if (storedToasts) {
        const toastArray = JSON.parse(storedToasts) as string[];
        this.completionToastShownSet = new Set(toastArray);
      }
    } catch (error) {
    }
  }

  /**
   * Save completion toast state to sessionStorage
   */
  private saveToastStateToStorage(): void {
    try {
      const toastArray = Array.from(this.completionToastShownSet);
      sessionStorage.setItem(this.TOAST_SHOWN_KEY, JSON.stringify(toastArray));
    } catch (error) {
    }
  }

  /**
   * Get current extraction runs
   */
  getRecentRuns(): ExtractionRun[] {
    return this.recentRunsSubject.value;
  }

  /**
   * Add a new extraction run to the beginning of the list
   */
  addRun(run: ExtractionRun): void {
    const currentRuns = this.recentRunsSubject.value;
    this.recentRunsSubject.next([run, ...currentRuns]);
    this.saveRunsToStorage();
    
    // Start timer immediately if run is already in 'Running' status
    if (run.status === 'Running' && run.startedDate) {
      this.startTimer(run.id, run.startedDate);
    }
  }

  /**
   * Update an existing extraction run by id
   */
  updateRun(runId: string, updates: Partial<ExtractionRun>): void {
    const currentRuns = this.recentRunsSubject.value;
    const updatedRuns = currentRuns.map(run =>
      run.id === runId ? { ...run, ...updates } : run
    );
    this.recentRunsSubject.next(updatedRuns);
    this.saveRunsToStorage();
    
    // Auto-manage timer based on status change
    const newStatus = updates.status;
    if (newStatus === 'Running' && !this.timerIntervals.has(runId)) {
      const run = updatedRuns.find(r => r.id === runId);
      if (run) {
        this.startTimer(runId, run.startedDate);
      }
    } else if ((newStatus === 'Completed' || newStatus === 'Failed') && this.timerIntervals.has(runId)) {
      this.stopTimer(runId);
    }
  }

  /**
   * Update multiple runs at once based on their IDs
   */
  updateRuns(updates: { id: string; data: Partial<ExtractionRun> }[]): void {
    let currentRuns = this.recentRunsSubject.value;
    
    updates.forEach(({ id, data }) => {
      currentRuns = currentRuns.map(run =>
        run.id === id ? { ...run, ...data } : run
      );
      
      // Auto-manage timer based on status change
      const newStatus = data.status;
      if (newStatus === 'Running' && !this.timerIntervals.has(id)) {
        const run = currentRuns.find(r => r.id === id);
        if (run) {
          this.startTimer(id, run.startedDate);
        }
      } else if ((newStatus === 'Completed' || newStatus === 'Failed') && this.timerIntervals.has(id)) {
        this.stopTimer(id);
      }
    });
    
    this.recentRunsSubject.next(currentRuns);
    this.saveRunsToStorage();
  }

  /**
   * Clear all extraction runs (called on logout)
   */
  clearRuns(): void {
    this.recentRunsSubject.next([]);
    this.completionToastShownSet.clear();
    this.extractionCompleted$.complete();
    
    // Clear all active timers
    this.timerIntervals.forEach((interval) => clearInterval(interval));
    this.timerIntervals.clear();
    this.timersSubject.next(new Map());
    
    // Clear sessionStorage
    sessionStorage.removeItem(this.STORAGE_KEY);
    sessionStorage.removeItem(this.TOAST_SHOWN_KEY);
  }

  /**
   * Check if completion toast has already been shown for a run
   */
  hasShownCompletionToast(runId: string): boolean {
    return this.completionToastShownSet.has(runId);
  }

  /**
   * Mark that completion toast has been shown for a run
   */
  markCompletionToastShown(runId: string): void {
    this.completionToastShownSet.add(runId);
    this.saveToastStateToStorage();
  }

  /**
   * Get a run by ID
   */
  getRunById(id: string): ExtractionRun | undefined {
    return this.recentRunsSubject.value.find(run => run.id === id);
  }

  /**
   * Get running extractions
   */
  getRunningExtractions(): ExtractionRun[] {
    return this.recentRunsSubject.value.filter(run => run.status === 'Running');
  }

  /**
   * Set the currently selected run for result page navigation
   */
  setSelectedRun(run: ExtractionRun | null): void {
    this.selectedRunSubject.next(run);
  }

  /**
   * Get the currently selected run
   */
  getSelectedRun(): ExtractionRun | null {
    return this.selectedRunSubject.value;
  }

  /**
   * Set the source page for back button navigation
   * @param source - 'extraction-reports' or 'data-extraction'
   */
  setNavigationSource(source: string): void {
    this.navigationSource = source;
  }

  /**
   * Get the source page for back button tooltip
   */
  getNavigationSource(): string {
    return this.navigationSource;
  }

  // ============================================================================
  // PDF HANDLING METHODS - Fetch, Cache, and Display PDF from /execution_data
  // ============================================================================

  /**
   * Fetch PDF from /execution_data API
   * GET /api/v1/data/execution_data?request_id={id}&document_model={model}&data_type=pdfs
   * Returns PDF blob to display in viewer (not for download)
   * 
   * @param runId Extraction run ID
   * @param documentModel Document model name
   * @returns Observable<Blob> - PDF blob for display
   */
  fetchPdfFromApi(runId: string, documentModel: string): Observable<Blob> {
    // Emit loading state
    this.pdfLoadingSubject.next({ runId, isLoading: true });

    const url = `${this.apiUrlV1}/data/execution_data`;
    const params = new HttpParams()
      .set('request_id', runId)
      .set('document_model', documentModel)
      .set('data_type', 'pdfs');

    return new Observable(observer => {
      this.http.get(url, {
        params,
        responseType: 'blob',
        headers: {
          'accept': 'application/pdf'
        }
      }).subscribe({
        next: (blob: Blob) => {
          this.pdfLoadingSubject.next({ runId, isLoading: false });
          observer.next(blob);
          observer.complete();
        },
        error: (error: any) => {
          const errorMsg = error?.error?.message || error?.message || 'Failed to load PDF';
          this.pdfLoadingSubject.next({ runId, isLoading: false, error: errorMsg });
          observer.error(error);
        }
      });
    });
  }

  /**
   * Load PDF for a specific run - checks cache, fetches if needed
   * Updates run with PDFData, stores in cache, and updates runs observable
   * 
   * @param runId Extraction run ID
   * @param documentModel Document model name
   * @returns Observable<PDFData> - PDF data ready for display
   */
  loadPdfForRun(runId: string, documentModel: string): Observable<PDFData> {
    return new Observable(observer => {
      // Check if PDF is already cached
      const cachedPdf = this.pdfCache.get(runId);
      if (cachedPdf && !cachedPdf.error) {
        observer.next(cachedPdf);
        observer.complete();
        return;
      }

      // Fetch from API
      this.fetchPdfFromApi(runId, documentModel).subscribe({
        next: (blob: Blob) => {
          // Convert blob to blob URL for ng2-pdf-viewer
          const blobUrl = URL.createObjectURL(blob);
          const pdfData: PDFData = {
            blob,
            blobUrl,
            loadedAt: new Date(),
            isLoading: false
          };

          // Cache the PDF data
          this.pdfCache.set(runId, pdfData);

          // Update the run with PDF data
          this.updateRunPdfData(runId, pdfData);

          observer.next(pdfData);
          observer.complete();
        },
        error: (error: any) => {
          const errorMsg = error?.error?.message || error?.message || 'Failed to load PDF';
          const pdfData: PDFData = {
            blob: new Blob(),
            blobUrl: '',
            loadedAt: new Date(),
            isLoading: false,
            error: errorMsg
          };

          // Cache the error
          this.pdfCache.set(runId, pdfData);

          // Update the run with error
          this.updateRunPdfData(runId, pdfData);

          observer.error(error);
        }
      });
    });
  }

  /**
   * Poll extraction status until COMPLETED and emit completion event
   * Polls every 2 seconds until status is COMPLETED
   * Shows toaster exactly once when polling completes
   * 
   * @param runId Extraction run ID
   * @param documentModel Document model name
   * @param pollFn Function that returns observable of poll response
   * @param notificationFn Function to call for showing toaster (injected from component/caller)
   */
  pollUntilComplete(
    runId: string,
    documentModel: string,
    pollFn: (id: string) => Observable<any>,
    notificationFn: (docModel: string, runId: string) => void
  ): Observable<any> {
    // Clear the toast flag when starting a new polling cycle for this run
    // This ensures we'll show the toast if completion is detected during THIS polling cycle
    this.completionToastShownSet.delete(runId);
    return interval(2000).pipe(
      switchMap(() => {
        return pollFn(runId);
      }),
      filter((response: any) => {
        return response?.status === 'COMPLETED';
      }),
      first(),
      timeout(300000), // 5 minute timeout
      tap((response: any) => {
        // Show toaster as a side effect using tap - this is more reliable than switchMap
        if (!this.completionToastShownSet.has(runId)) {
          notificationFn(documentModel, runId);
          this.completionToastShownSet.add(runId);
        } else {
        }

        // Emit completion event
        this.extractionCompleted$.next({ runId, response });
      })
    );
  }

  /**
   * Get PDF data for a run (from cache if available)
   * 
   * @param runId Extraction run ID
   * @returns PDFData or undefined if not loaded
   */
  getPdfData(runId: string): PDFData | undefined {
    return this.pdfCache.get(runId);
  }

  /**
   * Update a run with PDF data
   * Merges PDF data into the run and notifies observers
   * 
   * @param runId Extraction run ID
   * @param pdfData PDF data to store in the run
   */
  private updateRunPdfData(runId: string, pdfData: PDFData): void {
    const currentRuns = this.recentRunsSubject.value;
    const updatedRuns = currentRuns.map(run =>
      run.id === runId ? { ...run, pdfData } : run
    );
    this.recentRunsSubject.next(updatedRuns);
    // Note: We don't persist pdfData to storage as it contains Blob/URL that can't be serialized
    // PDF will be re-fetched from API on page refresh if needed
  }

  /**
   * Release PDF blob URL to free memory (call when done viewing)
   * 
   * @param runId Extraction run ID
   */
  releasePdfResource(runId: string): void {
    const pdfData = this.pdfCache.get(runId);
    if (pdfData && pdfData.blobUrl) {
      URL.revokeObjectURL(pdfData.blobUrl);
    }
    this.pdfCache.delete(runId);
  }

  /**
   * Release all cached PDF resources (call on logout/cleanup)
   */
  releasAllPdfResources(): void {
    this.pdfCache.forEach((pdfData, runId) => {
      if (pdfData.blobUrl) {
        URL.revokeObjectURL(pdfData.blobUrl);
      }
    });
    this.pdfCache.clear();
  }

  /**
   * Clear PDF cache without revoking URLs
   * Useful for session reset without memory cleanup
   */
  clearPdfCache(): void {
    this.pdfCache.clear();
  }

  /**
   * Check if PDF is cached for a run
   * 
   * @param runId Extraction run ID
   * @returns true if PDF is cached and ready, false otherwise
   */
  isPdfCached(runId: string): boolean {
    const pdfData = this.pdfCache.get(runId);
    return !!pdfData && !pdfData.error && !!pdfData.blobUrl;
  }

  /**
   * Check if PDF is currently loading for a run
   * 
   * @param runId Extraction run ID
   * @returns true if PDF is being loaded
   */
  isPdfLoading(runId: string): boolean {
    const pdfData = this.pdfCache.get(runId);
    return pdfData?.isLoading || false;
  }

  /**
   * Get PDF loading error message if any
   * 
   * @param runId Extraction run ID
   * @returns Error message or undefined if no error
   */
  getPdfError(runId: string): string | undefined {
    const pdfData = this.pdfCache.get(runId);
    return pdfData?.error;
  }

  /**
   * Convert PDF blob to base64 string for alternative display method
   * 
   * @param blob PDF blob
   * @returns Promise<string> - Base64 encoded string
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Extract base64 part (remove data:application/pdf;base64, prefix)
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Get PDF as base64 string (alternative to blob URL)
   * Useful for inline display or API requests
   * 
   * @param runId Extraction run ID
   * @returns Promise<string> - Base64 encoded PDF string
   */
  async getPdfAsBase64(runId: string): Promise<string> {
    const pdfData = this.pdfCache.get(runId);
    if (!pdfData) {
      throw new Error(`No PDF data found for run ${runId}`);
    }
    return this.blobToBase64(pdfData.blob);
  }

  // ============================================================================
  // TIMER MANAGEMENT - Smooth 1-second duration updates for running extractions
  // ============================================================================

  /**
   * Start a timer for an extraction run that updates every 1 second
   * Timer runs from startedDate and emits formatted duration via timers$ observable
   * Should be called when run status changes to 'Running'
   * 
   * @param runId Extraction run ID
   * @param startedDate Run start date
   */
  startTimer(runId: string, startedDate: Date): void {
    // Skip if timer already running for this run (prevents duplicates)
    if (this.timerIntervals.has(runId)) {
      return;
    }

    // Convert to timestamp for comparison
    const startTime = new Date(startedDate).getTime();

    const updateTimer = () => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const formatted = this.formatDuration(elapsed);

      // Run timer update inside Angular's zone to trigger change detection
      this.ngZone.run(() => {
        // Update the timers map
        const timers = this.timersSubject.getValue();
        timers.set(runId, formatted);
        this.timersSubject.next(new Map(timers));
      });
    };

    // Update immediately
    updateTimer();

    // Run the interval outside Angular's zone for performance
    // Each tick calls updateTimer which runs inside the zone for change detection
    this.ngZone.runOutsideAngular(() => {
      const interval = setInterval(updateTimer, 1000);
      this.timerIntervals.set(runId, interval);
    });
  }

  /**
   * Stop the timer for an extraction run
   * Only stops the interval and clears from timers$ map
   * Does NOT update run.duration - let the API response (via updateRunStatuses) be the source of truth
   * Should be called when run status changes to 'Completed' or 'Failed'
   * 
   * @param runId Extraction run ID
   */
  stopTimer(runId: string): void {
    const interval = this.timerIntervals.get(runId);
    if (interval) {
      clearInterval(interval);
      this.timerIntervals.delete(runId);
    }

    // Remove from timers map so component stops showing timer
    const timers = this.timersSubject.getValue();
    timers.delete(runId);
    this.timersSubject.next(new Map(timers));
  }

  /**
   * Format elapsed seconds to human-readable duration string
   * Rounds all values to whole numbers with no decimals
   * Examples: "5s", "1m 30s", "2h 15m 45s"
   * 
   * @param seconds Elapsed seconds
   * @returns Formatted duration string
   */
  private formatDuration(seconds: number): string {
    if (seconds <= 0) return '0s';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.round(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  }
}

